class BrandSlider extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('[data-brand-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-brand-dot]'));
    this.updateUrl = this.dataset.updateUrl !== 'false';
    this.currentIndex = 0;
    this.isAnimating = false;
    this.wheelGestureLocked = false;
    this.touchGestureLocked = false;
    this.touchStartX = null;
    this.touchStartY = null;
    this.touchAxis = null;
    this.transitionMs = 750;
    this.wheelIdleMs = 180;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.mobileQuery = window.matchMedia('(max-width: 749px)');
    if (this.mobileQuery.matches) this.transitionMs = 600;

    if (this.slides.length === 0) return;

    this.boundOnPopState = this.onPopState.bind(this);
    this.boundOnKeyDown = this.onKeyDown.bind(this);
    this.boundOnWheel = this.onWheel.bind(this);
    this.boundOnTouchStart = this.onTouchStart.bind(this);
    this.boundOnTouchMove = this.onTouchMove.bind(this);
    this.boundOnTouchEnd = this.onTouchEnd.bind(this);

    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        if (this.mobileQuery.matches) {
          this.scrollToMobileSlide(index, { push: true });
        } else {
          this.goTo(index, { push: true });
        }
      });
    });

    window.addEventListener('popstate', this.boundOnPopState);
    this.addEventListener('keydown', this.boundOnKeyDown);
    if (!this.mobileQuery.matches) {
      this.addEventListener('wheel', this.boundOnWheel, { passive: false });
      this.addEventListener('touchstart', this.boundOnTouchStart, { passive: true });
      this.addEventListener('touchmove', this.boundOnTouchMove, { passive: false });
      this.addEventListener('touchend', this.boundOnTouchEnd, { passive: true });
      this.addEventListener('touchcancel', this.boundOnTouchEnd, { passive: true });
      this.setHeaderHidePrevention(true);
    }

    const initialIndex = this.indexFromPath(window.location.pathname);
    if (this.mobileQuery.matches) {
      this.setupMobileSlider(initialIndex);
    } else {
      this.goTo(initialIndex, { push: false, replace: true, align: false, animate: false });
    }
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this.boundOnPopState);
    this.removeEventListener('keydown', this.boundOnKeyDown);
    this.removeEventListener('wheel', this.boundOnWheel);
    this.removeEventListener('touchstart', this.boundOnTouchStart);
    this.removeEventListener('touchmove', this.boundOnTouchMove);
    this.removeEventListener('touchend', this.boundOnTouchEnd);
    this.removeEventListener('touchcancel', this.boundOnTouchEnd);
    window.clearTimeout(this.wheelIdleTimer);
    window.clearTimeout(this.animationTimer);
    window.clearTimeout(this.mobileScrollTimer);
    this.mobileTrack?.removeEventListener('scroll', this.boundOnMobileScroll);
    this.setHeaderHidePrevention(false);
  }

  setupMobileSlider(index) {
    this.mobileTrack = this.querySelector('.brand-slider__slides');
    if (!this.mobileTrack) return;

    this.slides.forEach((slide, slideIndex) => {
      this.resetSlideClasses(slide);
      slide.removeAttribute('hidden');
      slide.setAttribute('aria-hidden', 'false');
      slide.classList.toggle('is-active', slideIndex === index);
    });

    this.currentIndex = index;
    this.syncDots(index);
    this.syncUrl(index, { replace: true });
    this.boundOnMobileScroll = this.onMobileScroll.bind(this);
    this.mobileTrack.addEventListener('scroll', this.boundOnMobileScroll, { passive: true });

    requestAnimationFrame(() => {
      this.mobileTrack.scrollLeft = index * this.mobileTrack.clientWidth;
    });
  }

  scrollToMobileSlide(index, { push = false } = {}) {
    if (!this.mobileTrack || index < 0 || index >= this.slides.length) return;

    this.currentIndex = index;
    this.syncDots(index);
    this.syncUrl(index, { push });
    this.mobileTrack.scrollTo({
      left: index * this.mobileTrack.clientWidth,
      behavior: this.reducedMotion ? 'auto' : 'smooth',
    });
  }

  onMobileScroll() {
    window.clearTimeout(this.mobileScrollTimer);
    this.mobileScrollTimer = window.setTimeout(() => {
      const index = Math.round(this.mobileTrack.scrollLeft / this.mobileTrack.clientWidth);
      if (index === this.currentIndex || index < 0 || index >= this.slides.length) return;

      this.currentIndex = index;
      this.slides.forEach((slide, slideIndex) => slide.classList.toggle('is-active', slideIndex === index));
      this.syncDots(index);
      this.syncUrl(index, { replace: true });
    }, 100);
  }

  normalizePath(url) {
    if (!url) return '';

    try {
      const parsed = new URL(url, window.location.origin);
      return parsed.pathname.replace(/\/$/, '') || '/';
    } catch (error) {
      return String(url).replace(/\/$/, '');
    }
  }

  indexFromPath(pathname) {
    const current = this.normalizePath(pathname);
    const match = this.slides.findIndex((slide) => {
      const slidePath = this.normalizePath(slide.dataset.url);
      return slidePath && slidePath === current;
    });

    return match >= 0 ? match : 0;
  }

  getStickyHeader() {
    return document.querySelector('sticky-header');
  }

  setHeaderHidePrevention(enabled) {
    const stickyHeader = this.getStickyHeader();
    if (!stickyHeader) return;
    stickyHeader.preventHide = enabled;
    if (enabled && typeof stickyHeader.reveal === 'function') {
      stickyHeader.reveal();
    }
  }

  revealHeader() {
    const stickyHeader = this.getStickyHeader();
    if (stickyHeader && typeof stickyHeader.reveal === 'function') {
      stickyHeader.reveal();
    }
  }

  isAwayFromViewport() {
    return window.scrollY > 8;
  }

  alignToViewport() {
    // Solo reencuadra si realmente nos hemos ido al footer.
    // Si ya estamos arriba, scrollTo(0)/reveal provoca un salto del header.
    if (!this.isAwayFromViewport()) return;

    window.scrollTo({ top: 0, behavior: 'auto' });
    this.revealHeader();
  }

  syncDots(index) {
    this.dots.forEach((dot, dotIndex) => {
      const isActive = dotIndex === index;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-current', isActive ? 'true' : 'false');
      dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
      dot.tabIndex = isActive ? 0 : -1;
    });
  }

  syncUrl(index, { push = false, replace = false } = {}) {
    const activeSlide = this.slides[index];
    const nextUrl = activeSlide?.dataset.url;
    const nextTitle = activeSlide?.dataset.title;

    if (nextTitle) {
      document.title = nextTitle;
    }

    if (!this.updateUrl || !nextUrl) return;

    const path = this.normalizePath(nextUrl);
    if (path && path !== this.normalizePath(window.location.pathname)) {
      if (push) {
        history.pushState({ brandSliderIndex: index }, nextTitle || '', path);
      } else if (replace) {
        history.replaceState({ brandSliderIndex: index }, nextTitle || '', path);
      }
    } else if (replace) {
      history.replaceState({ brandSliderIndex: index }, nextTitle || '', window.location.pathname);
    }
  }

  resetSlideClasses(slide) {
    slide.classList.remove(
      'is-active',
      'is-entering',
      'is-leaving',
      'is-entering-next',
      'is-entering-prev',
      'is-leaving-next',
      'is-leaving-prev'
    );
    delete slide.dataset.direction;
  }

  showSlideInstant(index) {
    this.slides.forEach((slide, slideIndex) => {
      this.resetSlideClasses(slide);
      const isActive = slideIndex === index;
      slide.classList.toggle('is-active', isActive);
      slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      if (isActive) {
        slide.removeAttribute('hidden');
      } else {
        slide.setAttribute('hidden', '');
      }
    });
  }

  goTo(index, { push = false, replace = false, align = true, animate = true } = {}) {
    if (index < 0 || index >= this.slides.length) return false;
    if (index === this.currentIndex && !replace) return false;
    if (this.isAnimating && !replace) return false;

    const previousIndex = this.currentIndex;
    const shouldAnimate = animate && !this.reducedMotion && index !== previousIndex;
    const direction = index > previousIndex ? 'next' : 'prev';

    this.currentIndex = index;
    this.syncDots(index);
    this.syncUrl(index, { push, replace });

    if (align && !this.mobileQuery.matches && index !== previousIndex && this.isAwayFromViewport()) {
      this.alignToViewport();
    }

    if (!shouldAnimate) {
      this.showSlideInstant(index);
      return true;
    }

    this.isAnimating = true;

    const currentSlide = this.slides[previousIndex];
    const nextSlide = this.slides[index];

    nextSlide.removeAttribute('hidden');
    nextSlide.setAttribute('aria-hidden', 'false');
    this.resetSlideClasses(nextSlide);
    nextSlide.classList.add(`is-entering-${direction}`);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        currentSlide.setAttribute('aria-hidden', 'true');
        currentSlide.classList.remove('is-active');
        currentSlide.classList.add(`is-leaving-${direction}`);

        nextSlide.classList.remove(`is-entering-${direction}`);
        nextSlide.classList.add('is-active');
      });
    });

    window.clearTimeout(this.animationTimer);
    this.animationTimer = window.setTimeout(() => {
      this.slides.forEach((slide, slideIndex) => {
        if (slideIndex === index) {
          this.resetSlideClasses(slide);
          slide.classList.add('is-active');
          slide.removeAttribute('hidden');
          slide.setAttribute('aria-hidden', 'false');
          return;
        }

        this.resetSlideClasses(slide);
        slide.setAttribute('hidden', '');
        slide.setAttribute('aria-hidden', 'true');
      });

      this.isAnimating = false;

      // En la última slide liberamos el gesto para poder bajar al footer.
      if (index >= this.slides.length - 1) {
        this.releaseWheelGesture();
      }
    }, this.transitionMs);

    return true;
  }

  step(direction) {
    return this.goTo(this.currentIndex + direction, { push: true });
  }

  /**
   * Bloquea el gesto de rueda hasta que pare la activity y termine la animación.
   * Así un scroll largo/momentum solo cambia una diapositiva.
   */
  beginWheelGesture() {
    this.wheelGestureLocked = true;
    this.deferWheelGestureUnlock();
  }

  releaseWheelGesture() {
    this.wheelGestureLocked = false;
    window.clearTimeout(this.wheelIdleTimer);
  }

  deferWheelGestureUnlock() {
    window.clearTimeout(this.wheelIdleTimer);
    this.wheelIdleTimer = window.setTimeout(() => {
      if (this.isAnimating) {
        this.deferWheelGestureUnlock();
        return;
      }
      this.wheelGestureLocked = false;
    }, this.wheelIdleMs);
  }

  onPopState(event) {
    const index =
      typeof event.state?.brandSliderIndex === 'number'
        ? event.state.brandSliderIndex
        : this.indexFromPath(window.location.pathname);

    if (this.mobileQuery.matches) {
      this.scrollToMobileSlide(index);
    } else {
      this.goTo(index, { push: false, replace: true });
    }
  }

  onKeyDown(event) {
    if (this.isAnimating) return;

    if (this.mobileQuery.matches) {
      if (event.key === 'ArrowRight' && this.currentIndex < this.slides.length - 1) {
        event.preventDefault();
        this.scrollToMobileSlide(this.currentIndex + 1, { push: true });
      } else if (event.key === 'ArrowLeft' && this.currentIndex > 0) {
        event.preventDefault();
        this.scrollToMobileSlide(this.currentIndex - 1, { push: true });
      }
      return;
    }

    const atLast = this.currentIndex >= this.slides.length - 1;
    const atFirst = this.currentIndex === 0;

    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
      if (atLast || this.isAwayFromViewport()) {
        this.scrollPageBy(80);
        event.preventDefault();
        return;
      }
      event.preventDefault();
      this.step(1);
    }

    if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
      if (this.isAwayFromViewport()) {
        this.scrollPageBy(-80);
        event.preventDefault();
        return;
      }
      if (atFirst) {
        event.preventDefault();
        return;
      }
      event.preventDefault();
      this.step(-1);
    }
  }

  scrollPageBy(delta) {
    window.scrollBy({ top: delta, left: 0, behavior: 'auto' });
  }

  onWheel(event) {
    if (this.slides.length < 2) return;

    const delta = event.deltaY;
    if (delta === 0) return;

    const direction = delta > 0 ? 1 : -1;
    const atFirst = this.currentIndex === 0;
    const atLast = this.currentIndex >= this.slides.length - 1;
    const away = this.isAwayFromViewport();

    // Si ya bajamos hacia el footer, el scroll de página es libre (sin snap al top).
    if (away) {
      this.releaseWheelGesture();
      event.preventDefault();
      this.scrollPageBy(delta);
      return;
    }

    // Última slide + scroll abajo → salir al footer.
    if (direction > 0 && atLast && !this.isAnimating) {
      this.releaseWheelGesture();
      event.preventDefault();
      this.scrollPageBy(delta);
      return;
    }

    const canMove =
      (direction > 0 && !atLast) ||
      (direction < 0 && !atFirst);

    // Primera slide + scroll arriba → evita el rebote del navegador.
    const blockOverscrollTop = direction < 0 && atFirst;

    if (this.wheelGestureLocked || this.isAnimating) {
      if (canMove || blockOverscrollTop) {
        event.preventDefault();
        this.beginWheelGesture();
      }
      return;
    }

    if (blockOverscrollTop) {
      event.preventDefault();
      this.beginWheelGesture();
      return;
    }

    if (!canMove) return;

    event.preventDefault();
    this.beginWheelGesture();
    this.step(direction);
  }

  onTouchStart(event) {
    this.touchStartX = event.touches[0]?.clientX ?? null;
    this.touchStartY = event.touches[0]?.clientY ?? null;
    this.touchAxis = null;
    this.touchGestureLocked = false;
  }

  onTouchMove(event) {
    if (this.mobileQuery.matches) {
      this.onMobileTouchMove(event);
      return;
    }

    if (this.slides.length < 2 || this.touchStartY === null) return;

    const currentY = event.touches[0]?.clientY;
    if (typeof currentY !== 'number') return;

    const delta = this.touchStartY - currentY;
    const direction = delta > 0 ? 1 : -1;
    const atLast = this.currentIndex >= this.slides.length - 1;
    const away = this.isAwayFromViewport();

    if (away || (direction > 0 && atLast && !this.isAnimating)) {
      this.touchGestureLocked = false;
      return;
    }

    if (this.touchGestureLocked || this.isAnimating) {
      event.preventDefault();
      return;
    }

    if (Math.abs(delta) < 40) return;

    const canMove =
      (direction > 0 && !atLast) ||
      (direction < 0 && this.currentIndex > 0);

    if (!canMove) return;

    event.preventDefault();
    this.touchGestureLocked = true;
    this.touchStartY = null;
    this.step(direction);
  }

  onMobileTouchMove(event) {
    if (this.slides.length < 2 || this.touchStartX === null || this.touchStartY === null) return;

    const currentX = event.touches[0]?.clientX;
    const currentY = event.touches[0]?.clientY;
    if (typeof currentX !== 'number' || typeof currentY !== 'number') return;

    const deltaX = this.touchStartX - currentX;
    const deltaY = this.touchStartY - currentY;

    if (!this.touchAxis) {
      if (Math.max(Math.abs(deltaX), Math.abs(deltaY)) < 10) return;
      this.touchAxis = Math.abs(deltaX) > Math.abs(deltaY) ? 'horizontal' : 'vertical';
    }

    if (this.touchAxis === 'vertical') return;

    event.preventDefault();
    if (this.touchGestureLocked || this.isAnimating || Math.abs(deltaX) < 40) return;

    const direction = deltaX > 0 ? 1 : -1;
    const atFirst = this.currentIndex === 0;
    const atLast = this.currentIndex >= this.slides.length - 1;
    const canMove = (direction > 0 && !atLast) || (direction < 0 && !atFirst);

    if (!canMove) return;

    this.touchGestureLocked = true;
    this.touchStartX = null;
    this.touchStartY = null;
    this.step(direction);
  }

  onTouchEnd() {
    this.touchStartX = null;
    this.touchStartY = null;
    this.touchAxis = null;
    this.touchGestureLocked = false;
  }
}

customElements.define('brand-slider', BrandSlider);
